import { MoreVertical } from "lucide-react"
import { useSafeMutation } from "~/common/use-safe-mutation"
import { DELETE_USER_MUTATION } from "~/profile/my-profile-screen"
import { Button } from "~/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/ui/dropdown-menu"
import { useToast } from "~/ui/use-toast"

interface UserActionsDropdownProps {
  userId: string
}

export function UserActionsDropdown({ userId }: UserActionsDropdownProps) {
  const { toast } = useToast()
  const [deleteUser, { loading }] = useSafeMutation(DELETE_USER_MUTATION, {
    update(cache) {
      // Optimistically remove the user from all users queries in cache
      cache.modify({
        fields: {
          users(existingUsers, { readField }) {
            if (existingUsers?.edges) {
              return {
                ...existingUsers,
                edges: existingUsers.edges.filter(
                  (edge: any) => readField("id", edge.node) !== userId
                ),
              }
            }
            return existingUsers
          },
        },
      })
    },
  })

  const handleRemoveUser = async (e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      const result = await deleteUser({
        variables: {
          input: {
            id: userId,
          },
        },
      })

      if (result.data?.userDelete?.user) {
        toast({
          title: "User scheduled for deletion",
          description: "The user has been scheduled for deletion.",
        })
      }
    } catch (error) {
      console.error("Error deleting user:", error)
      toast({
        title: "Error",
        description: "Failed to delete user. Please try again.",
        variant: "destructive",
      })
    }
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={(e) => e.stopPropagation()}
        >
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Open menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={handleRemoveUser} disabled={loading} className="text-red-600">
          {loading ? "Removing..." : "Remove User"}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
